import { ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Keeps quick nav pill active states in sync with the current collection URL.
 * Facet updates only re-render the collection section, not quick nav.
 */
const syncQuickNavActiveState = () => {
  const url = new URL(window.location.href);
  const explicitSort = url.searchParams.get('sort_by');

  document.querySelectorAll('[data-nf-quick-nav-link]').forEach((link) => {
    if (!(link instanceof HTMLAnchorElement)) return;

    const type = link.dataset.nfLinkType;
    let isActive = false;

    if (type === 'filter') {
      const param = link.dataset.nfFilterParam;
      const value = link.dataset.nfFilterValue;
      if (param && value) {
        isActive = url.searchParams.getAll(param).includes(value);
      }
    } else if (type === 'sort') {
      const sortBy = link.dataset.nfSortBy;
      isActive = Boolean(explicitSort && sortBy && explicitSort === sortBy);
    }

    const activeClass = link.dataset.nfActiveClass || 'nf-collection-quick-nav__link--active';
    link.classList.toggle(activeClass, isActive);
    if (isActive) {
      link.setAttribute('aria-current', 'page');
    } else {
      link.removeAttribute('aria-current');
    }
  });
};

/**
 * @param {string} sectionId
 */
const renderFramesSection = async (sectionId) => {
  sectionRenderer.clearSectionCache(sectionId);
  await sectionRenderer.renderSection(sectionId, { cache: false });
  syncQuickNavActiveState();
};

/**
 * Collection frames: update filters without a full page reload (keeps scroll).
 * @param {MouseEvent} event
 */
const onFramesNavClick = async (event) => {
  if (event.defaultPrevented) return;
  if (event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const target = event.target;
  if (!(target instanceof Element)) return;

  const link = target.closest('[data-nf-quick-nav-link]');
  if (!(link instanceof HTMLAnchorElement)) return;

  const frames = link.closest('[data-nf-collection-frames]');
  if (!(frames instanceof HTMLElement)) return;

  const sectionId = frames.getAttribute('section-id');
  if (!sectionId || link.getAttribute('href') == null) return;

  event.preventDefault();

  const url = new URL(link.href, window.location.origin);
  url.hash = '';

  const scrollY = window.scrollY;
  history.pushState({}, '', url.toString());
  syncQuickNavActiveState();

  try {
    frames.classList.add('is-loading');
    await renderFramesSection(sectionId);
    window.scrollTo(0, scrollY);
  } catch (error) {
    console.error(error);
    window.location.assign(url.toString());
  } finally {
    const nextFrames = document.querySelector('[data-nf-collection-frames]');
    if (nextFrames instanceof HTMLElement) {
      nextFrames.classList.remove('is-loading');
    }
  }
};

/** @type {boolean} */
let framesLoadMoreBusy = false;

/**
 * Append the next page of frame cards without changing the browser URL.
 * @param {HTMLElement} frames
 * @param {HTMLButtonElement} button
 */
const loadMoreFrames = async (frames, button) => {
  if (framesLoadMoreBusy) return;

  const sectionId = frames.getAttribute('section-id');
  const grid = frames.querySelector('[data-nf-frames-grid]');
  if (!sectionId || !(grid instanceof HTMLElement)) return;

  const lastPage = Number(grid.dataset.lastPage);
  const cards = grid.querySelectorAll('.collection-frames__card[data-page]');
  const lastCard = cards[cards.length - 1];
  const currentPage = Number(lastCard?.dataset.page || grid.dataset.currentPage);
  const nextPage = currentPage + 1;

  if (!lastPage || nextPage > lastPage) {
    const container = frames.querySelector('[data-nf-frames-load-more-container]');
    if (container instanceof HTMLElement) {
      container.hidden = true;
    }
    return;
  }

  framesLoadMoreBusy = true;
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  if (!button.dataset.defaultLabel) {
    button.dataset.defaultLabel = button.getAttribute('aria-label') || '';
  }
  if (button.dataset.loadingLabel) {
    button.setAttribute('aria-label', button.dataset.loadingLabel);
  }

  try {
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(nextPage));
    url.searchParams.delete('section_id');
    url.hash = '';

    const sectionHTML = await sectionRenderer.getSectionHTML(sectionId, false, url);
    const parsed = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const nextGrid = parsed.querySelector('[data-nf-frames-grid]');
    const nextCards = nextGrid?.querySelectorAll('.collection-frames__card[data-page]');

    if (!nextCards?.length) {
      const container = frames.querySelector('[data-nf-frames-load-more-container]');
      if (container instanceof HTMLElement) {
        container.hidden = true;
      }
      return;
    }

    const firstNewCard = nextCards[0];
    grid.append(...nextCards);
    grid.dataset.currentPage = String(nextPage);

    if (nextPage >= lastPage) {
      const container = frames.querySelector('[data-nf-frames-load-more-container]');
      if (container instanceof HTMLElement) {
        container.hidden = true;
      }
      if (firstNewCard instanceof HTMLElement) {
        firstNewCard.focus();
      }
    }
  } catch (error) {
    console.error(error);
  } finally {
    framesLoadMoreBusy = false;
    button.disabled = false;
    button.removeAttribute('aria-busy');
    if (button.dataset.defaultLabel) {
      button.setAttribute('aria-label', button.dataset.defaultLabel);
    }
  }
};

/**
 * Collection frames: load the next page of products.
 * @param {MouseEvent} event
 */
const onFramesLoadMoreClick = (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const button = target.closest('[data-nf-frames-load-more]');
  if (!(button instanceof HTMLButtonElement)) return;

  const frames = button.closest('[data-nf-collection-frames]');
  if (!(frames instanceof HTMLElement)) return;

  event.preventDefault();
  loadMoreFrames(frames, button);
};

/**
 * Re-render frames section on back/forward without jumping to top.
 */
const onFramesPopState = async () => {
  const frames = document.querySelector('[data-nf-collection-frames]');
  if (!(frames instanceof HTMLElement)) {
    syncQuickNavActiveState();
    return;
  }

  const sectionId = frames.getAttribute('section-id');
  if (!sectionId) {
    syncQuickNavActiveState();
    return;
  }

  const scrollY = window.scrollY;

  try {
    frames.classList.add('is-loading');
    await renderFramesSection(sectionId);
    window.scrollTo(0, scrollY);
  } catch (error) {
    console.error(error);
  } finally {
    const nextFrames = document.querySelector('[data-nf-collection-frames]');
    if (nextFrames instanceof HTMLElement) {
      nextFrames.classList.remove('is-loading');
    }
  }
};

document.addEventListener('DOMContentLoaded', syncQuickNavActiveState);
document.addEventListener(ThemeEvents.FilterUpdate, syncQuickNavActiveState);
document.addEventListener('click', onFramesNavClick);
document.addEventListener('click', onFramesLoadMoreClick);
window.addEventListener('popstate', onFramesPopState);

document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;
  if (target.querySelector('.nf-collection-quick-nav') || target.querySelector('[data-nf-collection-frames]')) {
    syncQuickNavActiveState();
  }
});
